/**
 * useAuth - Custom hook for authentication
 * Provides easy access to auth context and operations
 */

import { useContext } from 'react';
import { AuthContext } from '../components/auth/AuthProvider';
import type { AuthContextValue } from '../types/auth.types';

/**
 * Hook to access auth context
 * @throws {Error} If used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

/**
 * Hook for login operation
 */
export function useLogin() {
  const { login, isLoading, error, clearError } = useAuth();

  return {
    login,
    isLoading,
    error,
    clearError,
  };
}

/**
 * Hook for logout operation
 */
export function useLogout() {
  const { logout, isLoading } = useAuth();

  return {
    logout,
    isLoading,
  };
}

/**
 * Hook for current user
 */
export function useCurrentUser() {
  const { user, isAuthenticated, isLoading } = useAuth();

  return {
    user,
    isAuthenticated,
    isLoading,
  };
}

/**
 * Hook for session management
 */
export function useSession() {
  const { checkSession, refreshSession, isLoading } = useAuth();

  return {
    checkSession,
    refreshSession,
    isLoading,
  };
}

export default useAuth;
